import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Facebook, Instagram, FileText, Car, Camera, ChevronDown, ChevronUp, History, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const KIND = { article: { label: 'Article', Icon: FileText }, vehicle: { label: 'Véhicule', Icon: Car }, photo: { label: 'Photo client', Icon: Camera } };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('fr-CA', { dateStyle: 'medium', timeStyle: 'short' }) : '');
const thumb = (u) => (u && u.includes('/upload/') ? u.replace('/upload/', '/upload/w_120,h_90,c_fill,q_auto,f_auto/') : u);

const NetBadge = ({ net, Icon, name, testid }) => {
  if (!net) return <span className="text-xs text-slate-300 inline-flex items-center gap-1" data-testid={testid}><Icon className="h-3.5 w-3.5" /> —</span>;
  const ok = net.status === 'posted';
  const body = (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`} title={net.error || ''} data-testid={testid}>
      <Icon className="h-3.5 w-3.5" /> {ok ? `${name} · ${fmt(net.at)}` : `Échec · ${fmt(net.at)}`}
    </span>
  );
  return ok && net.url ? <a href={net.url} target="_blank" rel="noreferrer">{body}</a> : body;
};

const Row = ({ item, onDelete }) => {
  const { label, Icon } = KIND[item.kind];
  const err = item.facebook?.error || item.instagram?.error;
  return (
    <li className="flex items-center gap-3 py-2.5" data-testid={`history-item-${item.id}`}>
      {item.image ? <img src={thumb(item.image)} alt="" className="w-14 h-10 rounded object-cover bg-slate-100 shrink-0" /> : <div className="w-14 h-10 rounded bg-slate-100 shrink-0" />}
      <div className="min-w-0 flex-1">
        <a href={item.link} target="_blank" rel="noreferrer" className="text-sm font-medium text-slate-900 hover:text-blue-600 line-clamp-1">{item.title}</a>
        <div className="flex flex-wrap items-center gap-1.5 mt-1">
          <span className="inline-flex items-center gap-1 text-[11px] text-slate-500"><Icon className="h-3 w-3" /> {label}</span>
          <NetBadge net={item.facebook} Icon={Facebook} name="Facebook" testid={`history-fb-${item.id}`} />
          <NetBadge net={item.instagram} Icon={Instagram} name="Instagram" testid={`history-ig-${item.id}`} />
        </div>
        {err && <p className="text-[11px] text-red-600 mt-1 line-clamp-2" data-testid={`history-error-${item.id}`}>{err}</p>}
      </div>
      {item.kind === 'article' && (
        <button onClick={() => onDelete(item)} title="Supprimer l'article du blog" className="p-1.5 rounded text-red-600 hover:bg-red-50 shrink-0" data-testid={`history-delete-${item.id}`}>
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </li>
  );
};

export const SocialHistory = () => {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(true);
  const [all, setAll] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/social-history`, { headers: auth() }).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, []);

  const shown = all ? items : items?.slice(0, 8);
  const remove = async (item) => {
    if (!window.confirm(`Supprimer « ${item.title} » du blog? (Les publications déjà faites sur Facebook/Instagram restent en ligne.)`)) return;
    try {
      await axios.delete(`${API}/blog/posts/${item.id}`, { headers: auth() });
      setItems((list) => list.filter((it) => !(it.kind === 'article' && it.id === item.id)));
      toast.success('Article supprimé du blog');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec');
    }
  };
  return (
    <section className="bg-white rounded-xl border border-slate-200 p-5" data-testid="social-history">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between" data-testid="social-history-toggle">
        <h2 className="font-semibold text-slate-900 flex items-center gap-2"><History className="h-4 w-4 text-blue-600" /> Historique des publications {items ? `(${items.length})` : ''}</h2>
        {open ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>
      {open && (
        items === null ? <p className="text-sm text-slate-400 py-4">Chargement…</p>
          : items.length === 0 ? <p className="text-sm text-slate-500 py-4" data-testid="social-history-empty">Aucune publication automatique pour le moment. Le premier article sera publié demain à 9 h.</p>
          : (
            <>
              <ul className="divide-y divide-slate-100 mt-3">{shown.map((it) => <Row key={`${it.kind}-${it.id}`} item={it} onDelete={remove} />)}</ul>
              {items.length > 8 && (
                <button onClick={() => setAll(!all)} className="mt-2 text-sm text-blue-600 hover:underline" data-testid="social-history-more">{all ? 'Voir moins' : `Voir tout (${items.length})`}</button>
              )}
            </>
          )
      )}
    </section>
  );
};
